// Shared browser/server validation. These are reporting hints, never identity,
// entitlement, or payment authority. No raw queries, fragments or referrer URLs.
export const ACQUISITION_COOKIE = "pcai_utm"
export const ACQUISITION_MAX_AGE = 7 * 24 * 60 * 60
export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const
export type Acquisition = Record<string, string>

export function optionalAnalyticsAllowed(
  preferences: { doNotTrack?: string | null; globalPrivacyControl?: boolean } = typeof navigator ===
  "undefined"
    ? {}
    : navigator
): boolean {
  return preferences.doNotTrack !== "1" && preferences.globalPrivacyControl !== true
}

export function marketingPath(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const path = value.split(/[?#]/)[0].replace(/\/$/, "") || "/"
  if (path.length > 200 || !/^\/(?:[a-z0-9-]+\/)*[a-z0-9-]*$/.test(path)) return undefined
  if (
    /^\/(?:api|admin|auth|account|settings|dashboard|checkout|payment-success|payment-pending|signin|signup|reset-password|forgot-password|prompts|interview-history|debug-datadog)(?:\/|$)/.test(
      path
    )
  )
    return undefined
  if (/^\/mock-interview\/(?:booking|book|session|practice)(?:\/|$)/.test(path)) return undefined
  return path
}

function label(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const text = value.trim().slice(0, 100)
  return /^[a-zA-Z0-9_. +~-]{1,100}$/.test(text) ? text : undefined
}

export function sanitizeAcquisition(value: unknown): Acquisition {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {}
  const input = value as Record<string, unknown>
  const out: Acquisition = {}
  for (const key of [...UTM_KEYS, "acquisition_source", "acquisition_medium"]) {
    const safe = label(input[key])
    if (safe) out[key] = safe
  }
  const landing = marketingPath(input.landing_path)
  if (landing) out.landing_path = landing
  if (typeof input.referrer_host === "string" && /^[a-z0-9.-]{1,100}$/.test(input.referrer_host)) {
    out.referrer_host = input.referrer_host
  }
  return out
}

export function captureAcquisition(href: string, referrer: string): Acquisition {
  const url = new URL(href)
  const fields: Acquisition = {}
  for (const key of UTM_KEYS) {
    const safe = label(url.searchParams.get(key))
    if (safe) fields[key] = safe
  }
  const landing = marketingPath(url.pathname)
  if (landing) fields.landing_path = landing
  let host = ""
  try {
    const ref = new URL(referrer)
    const siteHost = (name: string) => name.toLowerCase().replace(/^www\./, "")
    if (/^https?:$/.test(ref.protocol) && siteHost(ref.hostname) !== siteHost(url.hostname))
      host = ref.hostname.toLowerCase()
  } catch {
    /* No usable external referrer. */
  }
  if (host.length <= 100 && /^[a-z0-9.-]+$/.test(host)) fields.referrer_host = host
  const isHost = (domain: string) => host === domain || host.endsWith(`.${domain}`)
  let source = host || "direct"
  let medium = host ? "referral" : "none"
  if (/^(?:www\.)?google\.(?:com|[a-z]{2}|co\.[a-z]{2}|com\.[a-z]{2})$/.test(host)) {
    source = "google"
    medium = "organic"
  } else if (isHost("bing.com")) {
    source = "bing"
    medium = "organic"
  }
  for (const [domain, name] of [
    ["chatgpt.com", "chatgpt"],
    ["chat.openai.com", "chatgpt"],
    ["perplexity.ai", "perplexity"],
    ["claude.ai", "claude"],
    ["copilot.microsoft.com", "copilot"],
    ["gemini.google.com", "gemini"],
  ]) {
    if (isHost(domain)) {
      source = name
      medium = "ai_referral"
      break
    }
  }
  fields.acquisition_source = fields.utm_source || source
  fields.acquisition_medium = fields.utm_medium || (fields.utm_source ? "campaign" : medium)
  return sanitizeAcquisition(fields)
}

export function readAcquisitionRecord(raw: string, now = Date.now()): Acquisition | null {
  try {
    if (raw.length > 3000) return null
    const record = JSON.parse(raw)
    if (!record || typeof record !== "object" || typeof record.captured_at !== "number") return null
    if (record.captured_at > now || now - record.captured_at >= ACQUISITION_MAX_AGE * 1000)
      return null
    const fields = sanitizeAcquisition(record)
    return Object.keys(fields).length ? fields : null
  } catch {
    return null
  }
}

export function acquisitionFromRequest(request: Request): Acquisition {
  if (request.headers.get("dnt") === "1" || request.headers.get("sec-gpc") === "1") return {}
  try {
    const cookie = request.headers.get("cookie")?.match(/(?:^|;\s*)pcai_utm=([^;]+)/)?.[1]
    return cookie ? (readAcquisitionRecord(decodeURIComponent(cookie)) ?? {}) : {}
  } catch {
    return {}
  }
}
